/* eslint-disable camelcase */
import { ParsedUrlQuery } from "querystring";
import React, { useEffect, useState } from "react";
import { GetServerSidePropsContext, InferGetServerSidePropsType } from "next";
import getConfig from "next/config";
import NodeCache from "node-cache";
import { v4 as uuidv4 } from "uuid";
import { getActiveVariant } from "@themuse/experiment-client";
import { initSnowplowTracker, triggerSnowplowPageViewEvent } from "@themuse/snowplow-js-client";

import { searchResultsSelector } from "selectors";
import { formatLog } from "utils/logging";
import { getParams } from "utils/searchResults";
import { UserProvider, useUserState } from "hooks/UserContext";
import { formatJobsResponse } from "orm/jobs";
import {
    fetchApiCompanySuggestions,
    fetchApiLocationSuggestions,
    fetchJobs,
    formatCompanySuggestions,
    fetchCompanyAttributes
} from "../utils/api";
import {
    companyIndustryItems,
    convertPerksAndBeniftsToNestedFormat,
    diversityInclusionItems,
    flattenPerksAndBenefits,
    jobCategoryItems,
    postedDateRangeItems,
    searchRadiusItems,
} from "../utils/searchFacetItems";
import { getFacetValuesFromFacetItems } from "../utils/searchFacets";
import { FeatureFlagsProvider } from "../hooks/FeatureFlagsContext";
import { SearchFacetsProvider } from "../hooks/SearchFacetsContext";
import { SearchProvider } from "../hooks/SearchContext";
import { SearchResultsProvider } from "../hooks/SearchResultsContext";
import { Seo } from "../components/header/Seo";
import { SearchPageLayout } from "../components/content/SearchPageLayout";
import {
    generateRandomString,
    IMetadataObj,
    titleCase,
} from "../utils/strings";
import { getActiveFeatureFlags } from "../utils/featureFlags";

// Expire once a day, check every 5 minutes
const apiCache = new NodeCache({ stdTTL: 86400, checkperiod: 300 });

// Expire once a day, check every 60 minutes
const apiCacheHourly = new NodeCache({ stdTTL: 86400, checkperiod: 3600 });

const getInitialLocationSuggestions = async (
    query: ParsedUrlQuery
): Promise<{ [key: string]: { [key: string]: string } }> => {
    const userInput = query?.location as string;
    const locationSuggestions = await fetchApiLocationSuggestions(userInput);
    if (locationSuggestions.length) {
        return {
            location: locationSuggestions[0]
        };
    }
    return {};
};

/**
 * Convert the job level into an array of strings that the API can understand.
 */
const convertExperienceLevel = (level: string): string[] => {
    const result = level
        .split(",")
        .map((levelValue: string) => {
            if (["senior", "management"].includes(levelValue)) {
                return "senior,management";
            }
            return levelValue;
        });
    return [...new Set(result)];
};

interface LocationObj {
    label: string;
    latitude: string;
    longitude: string;
    value: string;
}

/**
 * Convert the selected location into an array of strings that the API can understand.
 */
const convertSelectedLocation = (location: LocationObj) => ({
    label: location.label === "Flexible / Remote" ? "Remote / Flexible" : location.label,
    latitude: parseFloat(location.latitude),
    longitude: parseFloat(location.longitude),
    remote_work_location_type: location.label === "Remote / Flexible"
        || location.label === "Flexible / Remote" ? "virtual" : "",
    value: location.value === "Flexible / Remote" ? "Remote / Flexible" : location.value,
});

const getInitialCompanySuggestions = async (companySearchString: string): Promise<{[key: string]: any}> => {
    const { data } = await fetchApiCompanySuggestions(companySearchString as string);
    const suggestedCompany = data.find((company) => company.value.short_name === companySearchString);
    if (suggestedCompany) {
        const { text, value } = suggestedCompany;

        return {
            company: {
                name: text,
                shortname: value.short_name
            }
        };
    }
    // Get a single job as a way of
    // making a job request by shortname.
    // We don't use CTS here because it's
    // expensive (money-wise).
    const { hits } = await fetchJobs({ company_short_name: companySearchString, limit: 1 }, false);

    if (hits.length) {
        const { company } = hits[0].hit;

        return {
            company: {
                name: company.name,
                shortname: company.short_name
            }
        };
    }

    // The assumption is that context.query.company is the shortname
    return {
        company: {
            name: companySearchString,
            shortname: companySearchString
        }
    };
};

export const getInitialFilterParams = (params: IMetadataObj) => Object.keys(params)
    .reduce((values, currentValue): IMetadataObj => {
        const localValues = values;

        if (!["location", "company"].includes(currentValue)) {
            const decodedParamValue = decodeURIComponent(params[currentValue]);
            // Format keyword
            let value = decodedParamValue;
            if (currentValue === "keyword") {
                value = titleCase(value.replace(/\+/g, " "));
            }
            localValues[currentValue] = value;
        }

        return localValues;
    }, {} as IMetadataObj);

export const getInitialSearchId = (reqUrl: string, reqHost: string) => {
    const parsedUrl = new URL(reqUrl || "", `http://${reqHost || "www.example.com"}`);
    const searchIdValue = parsedUrl.searchParams.get("_search_id");
    return searchIdValue || uuidv4();
};

const getCompanyAttributes = async () => {
    try {
        const cachedCompanyAttributes = apiCacheHourly.get("companyAttributes");
        const { publicRuntimeConfig: { appName, appEnv } } = getConfig();
        if (cachedCompanyAttributes === undefined) {
            const { perks_and_benefits } = await fetchCompanyAttributes();

            const formattedCompanyAttributes = {
                perks_and_benefits: convertPerksAndBeniftsToNestedFormat(perks_and_benefits),
            };

            apiCacheHourly.set("companyAttributes", formattedCompanyAttributes);

            if (appEnv !== "production") {
                console.info(`${appName} retrieved company attributes from API. Cache is expired.`);
            }

            return formattedCompanyAttributes;
        }

        if (appEnv !== "production") {
            console.info(`${appName} retrieved company attributes from cache.  Cache is fresh.`);
        }

        return cachedCompanyAttributes;
    } catch (error) {
        const msg = `${process.env.APP_NAME} was unable fetch company attributes.`;
        console.error(msg, JSON.stringify(formatLog({ error })));
        throw error;
    }
};

interface DropDownCompanies {
    label: string;
    value: string;
}

/**
 * Get the default list of companies from the company suggestion endpoint.
 * Cache it for a day.
 */
const getDefaultCompanies = async (): Promise<DropDownCompanies[]> => {
    const cachedDropDownCompanies: DropDownCompanies[] = apiCache.get("DropDownCompanies");
    const { publicRuntimeConfig: { appName, appEnv } } = getConfig();

    if (cachedDropDownCompanies === undefined) {
        try {
            const response = await fetchApiCompanySuggestions("a");
            const dropDownCompanies: DropDownCompanies[] = formatCompanySuggestions(response.data);
            apiCache.set("DropDownCompanies", dropDownCompanies);
        } catch (error) {
            const msg = `${appName} was unable to fetch companies.`;
            console.error(msg, JSON.stringify(formatLog({ error })));
            throw new Error(msg);
        }

        if (appEnv !== "production") {
            console.info(`${appName} retrieved default dropdown companies from API. Cache is expired.`);
        }
        return apiCache.get("DropDownCompanies");
    }

    if (appEnv !== "production") {
        console.info(`${appName} retrieved default dropdown companies from cache.  Cache is fresh.`);
    }
    return cachedDropDownCompanies;
};

const isValidJSON = (blob: string) => {
    try {
        JSON.parse(blob);
        return true;
    } catch (error) {
        const msg = `Search-Renderer could not parse the carmen cookie. ${blob} is not valid json.`;
        console.error(msg, JSON.stringify(formatLog({ error })));
        return false;
    }
};

const getCarmenDataFromContext = (context: GetServerSidePropsContext) => {
    const { req } = context;
    const getRegex = /carmen_location=(\{.*\})/;
    const setCookieHeader = context.res.getHeader("set-cookie");
    const defaultCarmenData = {
        city: "",
        region: "",
        country: "",
        dma: "",
        lat: null,
        lon: null,
        accuracy_radius: null
    };

    if ("carmen_location" in req.cookies && isValidJSON(req.cookies?.carmen_location ?? null)) {
        return JSON.parse(req.cookies.carmen_location);
    }

    if (setCookieHeader) {
        // Cookies set in middleware in NextJS are not usable
        // on th request in which they were set. We check the
        // set-cookie header in this case.
        const cookieData = `${setCookieHeader}`.match(getRegex)?.[1] ?? null;
        if (cookieData && isValidJSON(cookieData)) {
            return JSON.parse(cookieData);
        }
    }
    return defaultCarmenData;
};

/**
 * Fetches server-side properties used on the initial load.
 * These can be found at:
 * * `__NEXT_DATA__.props.pageProps`
 */
export const getServerSideProps = async (context: GetServerSidePropsContext) => {
    const { resolvedUrl, query, req: { headers: { cookie } } } = context as undefined as any;
    const { publicRuntimeConfig: { appName } } = getConfig();
    const {
        FEATURE_FLAG_CTS_SEARCH_ENABLED = false,
        FEATURE_FLAG_CTS_HIRING_ENABLED = false,
    } = getActiveFeatureFlags(context);
    const isHiring = context.req?.url.startsWith("/hiring/");
    const isSearch = context.req?.url.startsWith("/search/");
    const isCTSSearch = isSearch && FEATURE_FLAG_CTS_SEARCH_ENABLED;
    const isCTSHiring = isHiring && FEATURE_FLAG_CTS_HIRING_ENABLED;
    const ctsEnabled = isCTSSearch || isCTSHiring;
    const contextSearchParams = new URLSearchParams(context.req?.url.split("?")?.[1] ?? "");
    const page = parseInt(contextSearchParams.get("page"), 10);
    const from_params = page ? (page - 1) * 20 : null;
    const initialDropDownCompanies: DropDownCompanies[] = await getDefaultCompanies();
    let initialSearchParams = {};
    const carmenLocation = getCarmenDataFromContext(context);
    const preference = generateRandomString();

    if (query.location) {
        try {
            const initialLocationParams = await getInitialLocationSuggestions(query);
            initialSearchParams = {
                ...context.query,
                ...initialLocationParams
            };
        } catch (error) {
            const msg = `${appName} was unable to fetch location: ${query?.location}`;
            console.error(msg, JSON.stringify(formatLog({ context, error })));
            throw new Error(msg);
        }
    }

    if (query.company) {
        try {
            const initialCompanyParams = await getInitialCompanySuggestions(query?.company as string);
            initialSearchParams = {
                ...initialSearchParams,
                ...initialCompanyParams
            };
        } catch (error) {
            const msg = `${appName} was unable to fetch company: ${query?.company}`;
            console.error(msg, JSON.stringify(formatLog({ context, error })));
            throw new Error(msg);
        }
    }

    const companyAttributes = await getCompanyAttributes();

    initialSearchParams = {
        ...initialSearchParams,
        ...getInitialFilterParams(query as IMetadataObj),
        preference,
        searchId: getInitialSearchId(context.req?.url, context.req?.headers?.host)
    };

    const initialSearchState = setInitialSearchState(
        initialSearchParams,
        companyAttributes,
        initialDropDownCompanies,
        ctsEnabled,
    );

    const {
        filters,
        latlng,
        remoteOption,
        companyShortName,
        searchKeyword,
    } = searchResultsSelector(initialSearchState);

    const queryParams = getParams({
        filters,
        companyShortName,
        searchKeyword,
        remoteOption,
        latlng,
        searchApiPreference: preference,
        ctsEnabled,
        from_param: isHiring ? from_params : null,
    });

    let initialSearchResultsState = null;
    const jobsResponse = await fetchJobs(queryParams, ctsEnabled, cookie);
    initialSearchResultsState = formatJobsResponse(jobsResponse);

    return {
        props: {
            resolvedUrl,
            companyAttributes,
            initialSearchState,
            initialSearchResultsState,
            carmenLocation,
            featureFlags: {
                ...getActiveFeatureFlags(context)
            }
        }
    };
};

/**
 * Transforms initial search parameters into an object for use in search functionality.
 * `__NEXT_DATA__.props.pageProps.initialSearchState`
 */
export const setInitialSearchState = (
    initialSearchParams,
    companyAttributes,
    initialDropDownCompanies,
    ctsEnabled
) => {
    const {
        location = null,
        company = null,
        keyword = "",
        category = "",
        industry = "",
        level = "",
        perks = "",
        size = "",
        radius = "",
        "date-posted": postedDateRange = "",
        "company-leadership": companyLeadership = "",
        preference = "",
        searchId = ""
    } = initialSearchParams;
    const { perks_and_benefits } = companyAttributes;
    const experienceLevel = convertExperienceLevel(level);
    const radiusValues = getFacetValuesFromFacetItems(radius, searchRadiusItems);
    const initialRadius = location ? radiusValues[0] ?? "" : "";
    const postedDateRangeValues = getFacetValuesFromFacetItems(postedDateRange, postedDateRangeItems);
    const initialPostedDateRange = postedDateRangeValues[0] ?? "";
    const pagination = ctsEnabled ? {} : { from_param: 1, size: 20 };

    return {
        location: location ? {
            userInputValue: location.label,
            userSelectedLocation: convertSelectedLocation(location)
        } : {
            userInputValue: "",
            // we are not using the users location on the server.
            userSelectedLocation: {
                label: "",
                latitude: null,
                longitude: null,
                value: ""
            }
        },
        company: company ? {
            userSelectedCompany: {
                label: company.name,
                value: company.shortname
            },
            companySuggestions: initialDropDownCompanies
        } : {},
        keyword: {
            userInputValue: keyword || "",
            userSelectedKeyword: {
                label: keyword || "",
                value: keyword || ""
            }
        },
        filters: {
            experienceLevel,
            category: getFacetValuesFromFacetItems(
                category, jobCategoryItems
            ),
            industry: getFacetValuesFromFacetItems(industry, companyIndustryItems),
            perks: getFacetValuesFromFacetItems(
                perks,
                flattenPerksAndBenefits(perks_and_benefits)
            ),
            companyLeadership: getFacetValuesFromFacetItems(companyLeadership, diversityInclusionItems),
            companySize: size?.length ? decodeURIComponent(size).split(",") : [],
            radius: initialRadius,
            postedDateRange: initialPostedDateRange,
        },
        preference,
        searchId,
        ctsEnabled,
        ...pagination
    };
};

const snowplowPageData = {
    sp_page_is_external: false,
    sp_page_section: "search",
    sp_page_sponsor_id: null,
    sp_page_tab: null,
    sp_page_type: "collection",
    sp_experiment_experiment_id: "",
    sp_experiment_variant_id: ""
};

/**
 * Initializes the Snowplow tracker and triggers page view events based on user actions and experiment data.
 */
const SnowplowInit = () => {
    const user = useUserState();
    const { publicRuntimeConfig } = getConfig();
    const [pageViewTriggered, setPageViewTriggered] = useState(false);

    useEffect(() => {
        const enabledVariant = getActiveVariant(
            "RECENT_SEARCHES",
            // eslint-disable-next-line no-underscore-dangle
            window?.__NEXT_DATA__?.props?.pageProps?.experiments ?? []
        );
        const experimentData = enabledVariant
            ? {
                sp_experiment_experiment_id: "RECENT_SEARCHES",
                sp_experiment_variant_id: enabledVariant
            }
            : {};
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            ...snowplowPageData,
            ...experimentData
        });
        if (publicRuntimeConfig?.snowplowCollector) {
            initSnowplowTracker(publicRuntimeConfig.snowplowCollector);
        }
    }, []);

    useEffect(() => {
        window.sp_user_logged_in = user.logged_in ?? false;
        window.sp_user_id = user.id ?? null;
    }, [user]);

    // Note: Add snowplow pageview in a useEffect??
    // After a user api request attempt has been made??
    useEffect(() => {
        if (user.isInitialized && !pageViewTriggered) {
            triggerSnowplowPageViewEvent();
            setPageViewTriggered(true);
        }
    }, [user]);

    return null;
};

const Home = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    const {
        resolvedUrl,
        carmenLocation,
        companyAttributes,
        featureFlags,
        initialSearchResultsState,
        initialSearchState,
    } = props;
    const search = new URLSearchParams(resolvedUrl.split("?")?.[1] ?? "");
    const job = search.get("job");
    const page = search.get("page");

    return (
        <UserProvider userLocation={carmenLocation}>
            <FeatureFlagsProvider featureFlags={featureFlags}>
                <SearchFacetsProvider companyFacets={companyAttributes}>
                    <SearchProvider {...initialSearchState}>
                        <SearchResultsProvider {...initialSearchResultsState}>
                            <SnowplowInit />
                            <Seo {...{ resolvedUrl }} />
                            <SearchPageLayout page={parseInt(page || "1", 10)} job={job} />
                        </SearchResultsProvider>
                    </SearchProvider>
                </SearchFacetsProvider>
            </FeatureFlagsProvider>
        </UserProvider>
    );
};

export default Home;
